import React, { useEffect, useRef, useState } from "react";
import { Trash2, Edit3, Plus, Upload, Loader2 } from "lucide-react";
import { api, imgSrc } from "../lib/api";

const EMPTY = {
  number: 1,
  title: "",
  description: "",
  duration: "30 min",
  cover_image: "",
  audio_url: "",
  spotify_url: "",
  apple_url: "",
  deezer_url: "",
};

export default function PodcastManager() {
  const [episodes, setEpisodes] = useState([]);
  const [draft, setDraft] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [msg, setMsg] = useState("");

  const refresh = () =>
    api.get("/podcast/episodes", { params: { limit: 100 } }).then((r) => setEpisodes(r.data));

  useEffect(() => {
    refresh();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setMsg("");
    try {
      if (editingId) {
        await api.put(`/podcast/episodes/${editingId}`, draft);
        setMsg("Épisode mis à jour ✓");
      } else {
        await api.post("/podcast/episodes", draft);
        setMsg("Épisode publié ✓");
      }
      setDraft({ ...EMPTY, number: nextNumber(episodes) });
      setEditingId(null);
      refresh();
    } catch (e2) {
      setMsg("Erreur : " + (e2.response?.data?.detail || e2.message));
    }
  };

  const edit = (ep) => {
    setDraft({
      number: ep.number,
      title: ep.title,
      description: ep.description,
      duration: ep.duration,
      cover_image: ep.cover_image,
      audio_url: ep.audio_url,
      spotify_url: ep.spotify_url,
      apple_url: ep.apple_url,
      deezer_url: ep.deezer_url,
    });
    setEditingId(ep.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (ep) => {
    if (!window.confirm(`Supprimer l'épisode #${ep.number} ?`)) return;
    await api.delete(`/podcast/episodes/${ep.id}`);
    refresh();
  };

  return (
    <div className="grid lg:grid-cols-2 gap-10">
      <form
        onSubmit={save}
        data-testid="episode-form"
        className="brutal-card p-6 flex flex-col gap-4"
        style={{ background: "#FA6157" }}
      >
        <h2 className="font-display text-3xl text-[#FFFDF7]">
          {editingId ? `Éditer épisode #${draft.number}` : "Nouvel épisode podcast"}
        </h2>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Numéro" testid="ep-number" type="number" value={draft.number} onChange={(v) => setDraft({ ...draft, number: Number(v) || 0 })} />
          <Field label="Durée (ex: 47 min)" testid="ep-duration" value={draft.duration} onChange={(v) => setDraft({ ...draft, duration: v })} />
        </div>
        <Field label="Titre" testid="ep-title" value={draft.title} onChange={(v) => setDraft({ ...draft, title: v })} required />
        <label className="flex flex-col gap-1">
          <span className="text-xs uppercase font-bold tracking-widest text-[#FFFDF7]">Description</span>
          <textarea
            data-testid="ep-description"
            value={draft.description}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            className="border-2 border-black rounded-xl px-3 py-2 bg-white min-h-[100px]"
            required
          />
        </label>

        <FilePicker
          label="Cover de l'épisode"
          accept="image/*"
          testid="ep-cover"
          value={draft.cover_image}
          onChange={(v) => setDraft({ ...draft, cover_image: v })}
          showPreview
        />

        <FilePicker
          label="Fichier audio (MP3, max 80 Mo)"
          accept="audio/*"
          testid="ep-audio"
          value={draft.audio_url}
          onChange={(v) => setDraft({ ...draft, audio_url: v })}
        />

        <div className="grid gap-3">
          <Field label="Lien Spotify" testid="ep-spotify" value={draft.spotify_url} onChange={(v) => setDraft({ ...draft, spotify_url: v })} placeholder="https://open.spotify.com/episode/..." />
          <Field label="Lien Apple Podcasts" testid="ep-apple" value={draft.apple_url} onChange={(v) => setDraft({ ...draft, apple_url: v })} placeholder="https://podcasts.apple.com/..." />
          <Field label="Lien Deezer" testid="ep-deezer" value={draft.deezer_url} onChange={(v) => setDraft({ ...draft, deezer_url: v })} placeholder="https://www.deezer.com/..." />
        </div>

        {msg && <p data-testid="ep-msg" className="text-sm font-bold text-[#FFFDF7]">{msg}</p>}
        <div className="flex gap-3">
          <button data-testid="ep-submit" type="submit" className="brutal-btn brutal-btn-yellow">
            <Plus size={16} /> {editingId ? "Mettre à jour" : "Publier l'épisode"}
          </button>
          {editingId && (
            <button
              type="button"
              onClick={() => {
                setDraft({ ...EMPTY, number: nextNumber(episodes) });
                setEditingId(null);
              }}
              className="brutal-btn brutal-btn-cream"
            >
              Annuler
            </button>
          )}
        </div>
      </form>

      <div data-testid="episodes-list" className="flex flex-col gap-3">
        <h2 className="font-display text-3xl">Épisodes ({episodes.length})</h2>
        {episodes.map((ep) => (
          <div key={ep.id} className="brutal-card p-4 flex gap-3 items-center">
            <img src={imgSrc(ep.cover_image)} alt="" className="w-16 h-16 border-2 border-black rounded-lg object-cover" />
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase font-bold tracking-widest text-black/60">
                #{ep.number} · {ep.duration}
              </p>
              <p className="font-display text-lg leading-tight truncate">{ep.title}</p>
            </div>
            <button
              data-testid={`ep-edit-${ep.id}`}
              onClick={() => edit(ep)}
              className="p-2 border-2 border-black rounded-full hover:bg-[#FFD01E]"
            >
              <Edit3 size={16} />
            </button>
            <button
              data-testid={`ep-delete-${ep.id}`}
              onClick={() => remove(ep)}
              className="p-2 border-2 border-black rounded-full hover:bg-[#FA6157]"
            >
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function nextNumber(episodes) {
  if (!episodes.length) return 1;
  return Math.max(...episodes.map((e) => e.number)) + 1;
}

function Field({ label, value, onChange, testid, type = "text", placeholder, required }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs uppercase font-bold tracking-widest text-[#FFFDF7]">{label}</span>
      <input
        data-testid={testid}
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="border-2 border-black rounded-xl px-3 py-2 bg-white focus:outline-none"
        required={required}
      />
    </label>
  );
}

function FilePicker({ label, accept, value, onChange, testid, showPreview = false }) {
  const ref = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setErr("");
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", f);
      const { data } = await api.post("/upload", form);
      onChange(data.url);
    } catch (e2) {
      const d = e2.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Upload échoué");
    } finally {
      setUploading(false);
    }
  };

  const previewSrc = imgSrc(value);

  return (
    <div className="flex flex-col gap-2">
      <span className="text-xs uppercase font-bold tracking-widest text-[#FFFDF7]">{label}</span>
      <div className="flex gap-3 items-start">
        <button
          type="button"
          onClick={() => ref.current?.click()}
          data-testid={`${testid}-upload`}
          className="brutal-btn brutal-btn-yellow !py-2 !px-4 shrink-0"
          disabled={uploading}
        >
          {uploading ? (
            <>
              <Loader2 size={16} className="animate-spin" /> Envoi…
            </>
          ) : (
            <>
              <Upload size={16} /> Téléverser
            </>
          )}
        </button>
        <input
          ref={ref}
          type="file"
          accept={accept}
          onChange={handleFile}
          className="hidden"
          data-testid={`${testid}-file-input`}
        />
        <input
          data-testid={testid}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="…ou colle une URL"
          className="border-2 border-black rounded-xl px-3 py-2 bg-white focus:outline-none flex-1"
        />
      </div>
      {err && <p className="text-xs font-bold text-white">{err}</p>}
      {showPreview && previewSrc && (
        <div className="mt-2 w-28 h-28 border-2 border-black rounded-xl overflow-hidden">
          <img src={previewSrc} alt="aperçu" className="w-full h-full object-cover" />
        </div>
      )}
      {!showPreview && value && value.endsWith(".mp3") && (
        <audio controls src={previewSrc} className="w-full mt-1" />
      )}
    </div>
  );
}
